/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { Dispatch, SetStateAction } from 'react';

import { System } from '@/components/icons';

export type KahuListPagerProps = {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  /** Plural label shown after the count, e.g. "traces" / "prompts". */
  noun: string;
  /** Docked variant rendered above the input when the inline pager scrolls
   *  out of view: dark rounded bar, re-enabled pointer events. */
  floating?: boolean;
};

/**
 * Prev/Next pager shared by the Kahu trace and prompt lists. View-only — it
 * drives the page state from `usePagination`; nothing is fetched on click.
 */
export const KahuListPager = function ({
  page,
  setPage,
  totalPages,
  rangeStart,
  rangeEnd,
  total,
  noun,
  floating = false,
}: KahuListPagerProps) {
  const isPrevDisabled = page === 1;
  const isNextDisabled = page === totalPages;

  return (
    <div
      className={`flex items-center justify-center gap-3 text-body-sm ${
        floating
          ? 'pointer-events-auto mb-1 rounded-lg bg-[#061419] py-2 font-medium'
          : 'my-1'
      }`}
    >
      <button
        type="button"
        aria-label="Previous page"
        disabled={isPrevDisabled}
        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
        className="flex items-center gap-1 text-[#257C9D] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {System.ChevronLeftIcon('size-4 text-[#257C9D]')}
        Previous
      </button>

      <span className="text-form-input-disabled">
        {rangeStart}-{rangeEnd} of {total} {noun}
      </span>

      <button
        type="button"
        aria-label="Next page"
        disabled={isNextDisabled}
        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
        className="flex items-center gap-1 text-[#257C9D] disabled:cursor-not-allowed disabled:opacity-40"
      >
        Next
        {System.ChevronRightIcon('size-4 text-[#257C9D]')}
      </button>
    </div>
  );
};
